import { useRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { STICKY_TOOLBAR_VAR, TOOLBAR_TOP, useStickyOffset } from "../../lib/useStickyOffset";

/**
 * The filter row above a table.
 *
 * Search, a status select, two date inputs and an Export button previously sat next to each other
 * as four unrelated controls at three different heights — native date inputs in particular have
 * their own intrinsic height and inner padding, so they never lined up with the search field.
 *
 * This enforces one baseline: every direct child is 40px tall (`h-10`, matching Input and Select),
 * and the row wraps rather than overflowing on narrow screens. `items-end` rather than
 * `items-center` so labelled and unlabelled controls still align along their bottom edge.
 */
export function Toolbar({
  children,
  sticky,
  className,
}: {
  children: ReactNode;
  /** Pins the filter row below the page title so search stays reachable at any scroll depth. */
  sticky?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useStickyOffset(ref, STICKY_TOOLBAR_VAR, Boolean(sticky));

  return (
    <div
      ref={ref}
      // Offset comes from the measured title height, not a literal: at 360px the title wraps and
      // this row wraps onto three lines, so any hardcoded value would be wrong at most widths.
      style={sticky ? { top: TOOLBAR_TOP } : undefined}
      className={cn(
        "flex flex-wrap items-end gap-3 rounded-card border border-border bg-surface p-3",
        // z-20 matches the title's layer; being later in DOM order it draws above the table's
        // sticky column (z-[1]) and below the app header (z-30).
        sticky && "sticky z-20",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A toolbar cell that grows to fill leftover space — normally the search field. */
export function ToolbarGrow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("min-w-[180px] flex-1", className)}>{children}</div>;
}

/** Pushes everything after it to the right edge. Wraps to its own line on narrow screens. */
export function ToolbarSpacer() {
  return <div className="ml-auto" />;
}
